/**
 * InsufficientUsageModal — RC 1.38.
 *
 * Shown when the user clicks an action whose backend cost is greater
 * than their currently available usage (included + extra). Replaces
 * the RC 1.37 inline-only warning under Top Opportunity rows, which
 * was technically correct but easy to miss.
 *
 * Hard rules:
 *   - All numeric values come from the backend response: the action's
 *     required-usage cost (e.g. `usage_costs.application_pack`) and
 *     the user's available usage (`usage_remaining`). The component
 *     does NOT know that the application_pack cost is 8.
 *   - Copy uses "usage / included usage / extra usage" only. The word
 *     "credits" is intentionally absent — RC 1.33.C purged it from
 *     the product surface.
 *   - The "Add usage" CTA routes to `/account?tab=plan&addUsage=1`,
 *     where the plan section will auto-open the package selector.
 */
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Plus, X } from 'lucide-react';

export default function InsufficientUsageModal({
  open,
  onOpenChange,
  requiredUsage,
  availableUsage,
  // Optional context — surfaces a one-line lead-in like "to generate
  // this application pack". Used by Top Opportunities; defaulted so
  // the component works for any future caller too.
  actionLabel = 'this action',
}) {
  const navigate = useNavigate();

  const cancel = () => onOpenChange(false);
  const goAddUsage = () => {
    onOpenChange(false);
    // ?addUsage=1 tells AccountPlanSection to auto-open the package
    // selector on mount, saving the user a click.
    navigate('/account?tab=plan&addUsage=1');
  };

  // Numbers are rendered straight from the backend so we never lie
  // about the cost. We DO defend against null/undefined defensively
  // so a transient loading state never produces "undefined usage".
  const required  = Number.isFinite(requiredUsage)  ? requiredUsage  : null;
  const available = Number.isFinite(availableUsage) ? availableUsage : null;
  const sentence = required != null && available != null
    ? `You need ${required} ${required === 1 ? 'Credit' : 'Credits'} to generate ${actionLabel}. You currently have ${available}.`
    : `You don’t have enough Credits to generate ${actionLabel}.`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[460px]"
        data-testid="insufficient-usage-modal"
        data-required-usage={required ?? ''}
        data-available-usage={available ?? ''}
      >
        <DialogHeader>
          <DialogTitle data-testid="insufficient-usage-modal-title">
            Not enough Credits
          </DialogTitle>
          <DialogDescription
            className="text-sm text-ndor-ink-secondary leading-relaxed"
            data-testid="insufficient-usage-modal-body"
          >
            {sentence}
            {' '}
            Adding extra Credits continues immediately and never expires
            until used. Your plan’s included Credits refresh on its
            own schedule.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button
            variant="outline"
            onClick={cancel}
            data-testid="insufficient-usage-modal-cancel"
          >
            <X className="h-4 w-4 mr-1.5" />
            Cancel
          </Button>
          <Button
            onClick={goAddUsage}
            data-testid="insufficient-usage-modal-add-usage"
            className="bg-ndor-primary hover:bg-ndor-primary-hover text-ndor-primary-foreground"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Add Credits
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
